import { Section, EyebrowHeading } from "./Section";
import { Check } from "lucide-react";

const included = [
  "Strategi & innehållsplanering",
  "Manus och regi",
  "Inspelning med proffsutrustning",
  "Klipp, grading och ljudmix",
  "Musiklicens & grafik",
  "Leverans i alla format ni behöver",
  "Fast projektledare",
  "Inga timdebiteringar",
];

export function PricingIncluded() {
  return (
    <Section className="py-12 md:py-16">
      <div className="mx-auto max-w-6xl rounded-3xl border border-white/10 bg-surface/60 p-8 md:p-10">
        <div className="grid gap-8 md:grid-cols-[1fr_2fr] md:gap-12">
          <div>
            <div className="mb-3 text-xs uppercase tracking-[0.18em] text-mint">
              Ingår alltid
            </div>
            <h2 className="text-balance text-2xl font-semibold leading-tight md:text-3xl">
              Allt det som andra fakturerar separat — ingår.
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              Oavsett vilket Pulse-paket ni väljer får ni hela kedjan från idé till färdig fil.
              Inga överraskningsfakturor.
            </p>
          </div>
          <ul className="grid gap-3 sm:grid-cols-2">
            {included.map((i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-mint" strokeWidth={2.5} />
                <span>{i}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Section>
  );
}

export function PricingCompare() {
  const rows = [
    { label: "Prismodell", trad: "Engångskostnad per projekt", fenny: "Fast månadspris" },
    { label: "Ledtid till färdig film", trad: "6–12 veckor", fenny: "2–4 veckor i löpande rytm" },
    { label: "Strategi & planering", trad: "Faktureras separat", fenny: "Inbakat" },
    { label: "Antal leveranser", trad: "1 film per uppdrag", fenny: "2–6 klipp/mån" },
    { label: "Formatanpassning", trad: "Tilläggsoffert", fenny: "16:9, 9:16, 1:1 ingår" },
    { label: "Skalbarhet", trad: "Ny offert varje gång", fenny: "Skala upp, ner eller pausa" },
  ];

  return (
    <Section className="py-12 md:py-16">
      <EyebrowHeading
        eyebrow="Modellskillnad"
        title="Projektkostnad vs månadsrytm"
        lede="Det här är inte en konkurrentjämförelse — det är två olika sätt att köpa film. Båda fungerar. Vi tror på det andra."
        align="center"
      />

      <div className="mx-auto mt-12 max-w-4xl overflow-hidden rounded-3xl border border-white/10 bg-surface">
        <div className="grid grid-cols-[1.2fr_1fr_1fr] text-sm md:text-base">
          <div className="border-b border-white/10 bg-surface/60 px-5 py-4 text-xs uppercase tracking-widest text-muted-foreground md:px-6 md:py-5">&nbsp;</div>
          <div className="border-b border-white/10 bg-surface/60 px-5 py-4 text-xs uppercase tracking-widest text-muted-foreground md:px-6 md:py-5">
            Traditionell byrå
          </div>
          <div className="border-b border-white/10 bg-mint/[0.06] px-5 py-4 text-xs uppercase tracking-widest text-mint md:px-6 md:py-5">
            Fenny Pulse
          </div>

          {rows.map((r, idx) => (
            <div key={r.label} className="contents">
              <div className={`px-5 py-4 font-medium text-foreground md:px-6 md:py-5 ${idx < rows.length - 1 ? "border-b border-white/10" : ""}`}>
                {r.label}
              </div>
              <div className={`px-5 py-4 text-sm text-muted-foreground md:px-6 md:py-5 ${idx < rows.length - 1 ? "border-b border-white/10" : ""}`}>
                {r.trad}
              </div>
              <div className={`bg-mint/[0.04] px-5 py-4 text-sm text-foreground/95 md:px-6 md:py-5 ${idx < rows.length - 1 ? "border-b border-white/10" : ""}`}>
                {r.fenny}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

export const PRICING_FAQ = [
  {
    q: "Hur lång är bindningstiden?",
    a: "Ingen bindning. Pulse rullar månadsvis — säg upp när ni vill utan uppsägningstid.",
  },
  {
    q: "Vad händer om vi inte använder upp månaden?",
    a: "Leveranserna kan rulla över en månad framåt vid behov. Vi planerar tillsammans så det jämnas ut.",
  },
  {
    q: "Äger vi materialet?",
    a: "Ja. Allt slutmaterial är ert att använda fritt. Vi behåller rätten att visa utvalda klipp i vår portfolio om inget annat avtalas.",
  },
  {
    q: "Kan vi pausa eller byta plan?",
    a: "Ja — byt plan eller pausa när ni vill. Skala upp under kampanjperioder, skala ner när det är lugnare.",
  },
  {
    q: "Tillkommer det kostnader för resa eller specialutrustning?",
    a: "Vid behov, alltid transparent och godkänt innan vi bokar. Inom Sverige ingår normal resa och logistik i Pulse-abonnemang.",
  },
];

export function PricingFAQ() {
  return (
    <Section className="py-12 md:py-16">
      <EyebrowHeading
        eyebrow="Vanliga frågor om paket & avtal"
        title="Det ni faktiskt undrar."
        align="center"
      />
      <div className="mx-auto mt-12 max-w-3xl divide-y divide-white/10 overflow-hidden rounded-3xl border border-white/10 bg-surface">
        {PRICING_FAQ.map((f) => (
          <details key={f.q} className="group">
            <summary className="flex cursor-pointer items-center justify-between gap-4 px-6 py-5 text-base font-semibold transition-colors hover:bg-white/[0.03]">
              {f.q}
              <span className="text-mint transition-transform group-open:rotate-45">+</span>
            </summary>
            <div className="px-6 pb-6 text-sm leading-relaxed text-muted-foreground">{f.a}</div>
          </details>
        ))}
      </div>
    </Section>
  );
}
